import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiFetch } from "../../lib/api";
import { useAuth } from "../../components/LoginGate";
import { canSeeFinance } from "../../lib/constants";

type RangeMode = "all" | "month";
type ViewMode = "table" | "cards";

interface Row {
  id: number;
  client_name: string | null;
  file_reference: string | null;
  case_type: string | null;
  team_name: string | null;
  current_stage: string | null;
  file_status: string | null;
  file_open_date: string | null;
  file_close_date: string | null;
  total_case_value: number;
  amount_collected: number;
}

function formatCurrency(v: number) {
  return "£" + (v || 0).toLocaleString("en-GB", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function statusBadge(s: string | null) {
  if (!s) return "badge badge-gray";
  const sl = s.toLowerCase();
  if (sl === "open" || sl === "in-progress") return "badge badge-blue";
  if (sl === "closed" || sl === "settled") return "badge badge-green";
  if (sl === "in court") return "badge badge-yellow";
  if (sl === "dropped") return "badge badge-red";
  if (sl === "appealed") return "badge badge-purple";
  return "badge badge-gray";
}

function getMonthBounds(): { from: string; to: string } {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const todayStr = now.toISOString().split("T")[0];
  return { from: first.toISOString().split("T")[0], to: todayStr };
}

export default function CurrentSituationPage() {
  const { user } = useAuth();
  const canFinance = canSeeFinance(user?.role);
  const [mode, setMode] = useState<RangeMode>("all");
  const [view, setView] = useState<ViewMode>(() => {
    try { return (localStorage.getItem("cs_view") as ViewMode) || "table"; } catch { return "table"; }
  });
  useEffect(() => { try { localStorage.setItem("cs_view", view); } catch {} }, [view]);
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [teamFilter, setTeamFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const timer = useRef<number | null>(null);

  const load = useCallback(async () => {
    const { from, to } = mode === "month" ? getMonthBounds() : { from: "2000-01-01", to: "2099-12-31" };
    const params = new URLSearchParams({ from, to });
    try {
      const r = await apiFetch(`/api/reports/cases?${params}`);
      if (r.ok) {
        const data = (await r.json()) as Row[];
        setRows(Array.isArray(data) ? data : []);
        setLastRefreshed(new Date());
      }
    } finally {
      setLoading(false);
    }
  }, [mode]);

  useEffect(() => {
    load();
    if (timer.current) window.clearInterval(timer.current);
    timer.current = window.setInterval(load, 30000);
    return () => { if (timer.current) window.clearInterval(timer.current); };
  }, [load]);

  const teams = useMemo(
    () => Array.from(new Set(rows.map(r => r.team_name).filter(Boolean) as string[])).sort(),
    [rows]
  );
  const types = useMemo(
    () => Array.from(new Set(rows.map(r => r.case_type).filter(Boolean) as string[])).sort(),
    [rows]
  );
  const statuses = useMemo(
    () => Array.from(new Set(rows.map(r => r.file_status).filter(Boolean) as string[])).sort(),
    [rows]
  );

  const filtered = rows.filter(r => {
    if (teamFilter && r.team_name !== teamFilter) return false;
    if (typeFilter && r.case_type !== typeFilter) return false;
    if (statusFilter && r.file_status !== statusFilter) return false;
    return true;
  });

  const totalFee = filtered.reduce((s, r) => s + (r.total_case_value || 0), 0);
  const totalCollected = filtered.reduce((s, r) => s + (r.amount_collected || 0), 0);

  return (
    <div style={{ fontSize: 14 }}>
      <div className="page-header" style={{ gap: 12, flexWrap: "wrap" }}>
        <div>
          <h1 className="page-title" style={{ fontSize: 28 }}>Current Situation</h1>
          <p className="page-subtitle">
            Live view of all cases. Auto-refresh every 30s.
            {lastRefreshed && (
              <span style={{ marginLeft: 8, fontSize: 12, color: "var(--text-muted)" }}>
                · Updated {lastRefreshed.toLocaleTimeString("en-GB")}
              </span>
            )}
          </p>
        </div>
        <div style={{ display: "flex", gap: 4, alignItems: "center", background: "var(--bg-light)", borderRadius: 8, padding: 4 }}>
          {(["all", "month"] as RangeMode[]).map(m => (
            <button
              key={m}
              onClick={() => setMode(m)}
              style={{
                padding: "6px 14px",
                borderRadius: 6,
                border: "none",
                cursor: "pointer",
                fontSize: 13,
                fontWeight: mode === m ? 600 : 400,
                background: mode === m ? "#fff" : "transparent",
                color: mode === m ? "var(--primary)" : "var(--text-secondary)",
                boxShadow: mode === m ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
              }}
            >
              {m === "all" ? "All Cases" : "This Month"}
            </button>
          ))}
          <button className="btn btn-sm" style={{ marginLeft: 8 }} onClick={load} disabled={loading}>
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
        <div style={{ display: "flex", gap: 4, alignItems: "center", background: "var(--bg-light)", borderRadius: 8, padding: 4 }}>
          {(["table", "cards"] as ViewMode[]).map(v => (
            <button
              key={v}
              onClick={() => setView(v)}
              style={{
                padding: "6px 12px",
                borderRadius: 6,
                border: "none",
                cursor: "pointer",
                fontSize: 13,
                fontWeight: view === v ? 600 : 400,
                background: view === v ? "#fff" : "transparent",
                color: view === v ? "var(--primary)" : "var(--text-secondary)",
                boxShadow: view === v ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
              }}
              title={v === "table" ? "Table view" : "Card view"}
            >
              {v === "table" ? "Table" : "Cards"}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
        <select className="form-input" value={teamFilter} onChange={e => setTeamFilter(e.target.value)} style={{ maxWidth: 180 }}>
          <option value="">All Teams</option>
          {teams.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select className="form-input" value={typeFilter} onChange={e => setTypeFilter(e.target.value)} style={{ maxWidth: 160 }}>
          <option value="">All Types</option>
          {types.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select className="form-input" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ maxWidth: 180 }}>
          <option value="">All Statuses</option>
          {statuses.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <div style={{ marginLeft: "auto", display: "flex", gap: 18, fontSize: 13 }}>
          <span><strong>{filtered.length}</strong> cases</span>
          {canFinance && <span>Fee: <strong>{formatCurrency(totalFee)}</strong></span>}
          {canFinance && <span>Collected: <strong style={{ color: "var(--success)" }}>{formatCurrency(totalCollected)}</strong></span>}
          {canFinance && <span>Outstanding: <strong style={{ color: "var(--danger)" }}>{formatCurrency(totalFee - totalCollected)}</strong></span>}
        </div>
      </div>

      {view === "cards" ? (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: 12,
            maxHeight: "calc(100vh - 260px)",
            overflow: "auto",
            paddingRight: 4,
          }}
        >
          {filtered.length === 0 && (
            <div style={{ gridColumn: "1 / -1", textAlign: "center", padding: 40, color: "var(--text-muted)" }}>
              No cases match the current filters.
            </div>
          )}
          {filtered.map(r => (
            <div key={r.id} className="card" style={{ padding: 14, display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 15, lineHeight: 1.3 }}>{r.client_name || "—"}</div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>{r.file_reference || "—"}</div>
                </div>
                <span className={statusBadge(r.file_status)}>{r.file_status}</span>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, fontSize: 12 }}>
                {r.case_type && <span className="badge badge-blue">{r.case_type}</span>}
                {r.team_name && <span className="badge badge-gray">{r.team_name}</span>}
              </div>
              {r.current_stage && (
                <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                  <strong style={{ color: "var(--text-muted)", fontWeight: 500 }}>Stage: </strong>
                  {r.current_stage}
                </div>
              )}
              {canFinance && (
                <div style={{ display: "flex", justifyContent: "space-between", borderTop: "1px solid var(--border)", paddingTop: 8, fontSize: 13 }}>
                  <div>
                    <div style={{ fontSize: 10, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.4 }}>Fee</div>
                    <div style={{ fontWeight: 600 }}>{formatCurrency(r.total_case_value)}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 10, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.4 }}>Collected</div>
                    <div style={{ fontWeight: 600, color: "var(--success)" }}>{formatCurrency(r.amount_collected)}</div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
      <div className="card" style={{ overflow: "hidden" }}>
        <div style={{ maxHeight: "calc(100vh - 260px)", overflow: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead style={{ position: "sticky", top: 0, background: "var(--bg-light)", zIndex: 1 }}>
              <tr>
                <th style={{ textAlign: "left", padding: "10px 12px", borderBottom: "1px solid var(--border)" }}>Client</th>
                <th style={{ textAlign: "left", padding: "10px 12px", borderBottom: "1px solid var(--border)" }}>File Ref</th>
                <th style={{ textAlign: "left", padding: "10px 12px", borderBottom: "1px solid var(--border)" }}>Team</th>
                <th style={{ textAlign: "left", padding: "10px 12px", borderBottom: "1px solid var(--border)" }}>Type</th>
                <th style={{ textAlign: "left", padding: "10px 12px", borderBottom: "1px solid var(--border)" }}>Stage</th>
                <th style={{ textAlign: "left", padding: "10px 12px", borderBottom: "1px solid var(--border)" }}>Status</th>
                {canFinance && <th style={{ textAlign: "right", padding: "10px 12px", borderBottom: "1px solid var(--border)" }}>Fee</th>}
                {canFinance && <th style={{ textAlign: "right", padding: "10px 12px", borderBottom: "1px solid var(--border)" }}>Collected</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={canFinance ? 8 : 6} style={{ textAlign: "center", padding: 40, color: "var(--text-muted)" }}>No cases match the current filters.</td></tr>
              )}
              {filtered.map(r => (
                <tr key={r.id}>
                  <td style={{ padding: "8px 12px", fontWeight: 600, borderBottom: "1px solid var(--border)" }}>{r.client_name || "—"}</td>
                  <td style={{ padding: "8px 12px", borderBottom: "1px solid var(--border)", color: "var(--text-secondary)" }}>{r.file_reference || "—"}</td>
                  <td style={{ padding: "8px 12px", borderBottom: "1px solid var(--border)" }}>{r.team_name || "—"}</td>
                  <td style={{ padding: "8px 12px", borderBottom: "1px solid var(--border)" }}>{r.case_type || "—"}</td>
                  <td style={{ padding: "8px 12px", borderBottom: "1px solid var(--border)" }}>
                    {r.current_stage
                      ? <span style={{ fontSize: 12, background: "var(--bg-light)", padding: "2px 8px", borderRadius: 4, border: "1px solid var(--border)" }}>{r.current_stage}</span>
                      : <span style={{ color: "var(--text-muted)" }}>—</span>}
                  </td>
                  <td style={{ padding: "8px 12px", borderBottom: "1px solid var(--border)" }}>
                    <span className={statusBadge(r.file_status)}>{r.file_status}</span>
                  </td>
                  {canFinance && <td style={{ padding: "8px 12px", borderBottom: "1px solid var(--border)", textAlign: "right" }}>{formatCurrency(r.total_case_value)}</td>}
                  {canFinance && <td style={{ padding: "8px 12px", borderBottom: "1px solid var(--border)", textAlign: "right", color: "var(--success)" }}>{formatCurrency(r.amount_collected)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      )}
    </div>
  );
}
